import { ResourceStatus, ResourceStatusLabels } from '@navio/nsdk';
import type { SitePortRecordListWithStorage } from 'noddi-async';
import { NavioOverlayActions, useAppForm } from 'noddi-ui-web';
import { constObjectToOptions } from 'noddi-util';
import { useState } from 'react';
import { z } from 'zod';
import { PortPinFields } from './PortPinFields';
import type { StorageSelectOption } from './useSitePortStorageOptions';
import { useStorageAreaBulkExpand } from './useStorageAreaBulkExpand';

const FORM_ID = 'site-port-form';

const resourceStatusOptions = constObjectToOptions(ResourceStatus, ResourceStatusLabels);

const storageOptionSchema = z.object({
  value: z.number(),
  label: z.string()
});

const validationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  providerInstructions: z.string(),
  parkingInstructions: z.string(),
  status: z.enum(['ACTIVE', 'DISABLED_HIDE', 'DISABLED_SHOW', 'OUT_OF_ORDER']),
  /** UI-only bulk-select; expands to storageUnits — never sent to the API. */
  bulkStorageAreaIds: z.array(z.number()),
  storageUnits: z.array(storageOptionSchema),
  positionIds: z.array(z.number()),
  workstationIds: z.array(z.number())
});

type FormValues = z.input<typeof validationSchema>;

/** Leaf mapping fields both create and edit write. Areas are not API targets. */
export type SitePortFormValues = {
  name: string;
  providerInstructions: string;
  parkingInstructions: string;
  parkingLatitude: number | null;
  parkingLongitude: number | null;
  latitude: number;
  longitude: number;
  storageUnitIds: number[];
  positionIds: number[];
  workstationIds: number[];
};

type Coordinates = { latitude: number; longitude: number };

export type PortFormLocationMappingProps = {
  hasStorageFacility: boolean;
  storageFacilityName?: string | null;
  storageAreaOptions: StorageSelectOption[];
  storageAreaNameById: Map<number, string>;
  storageUnitOptions: StorageSelectOption[];
  isUnitsPending?: boolean;
  onUnitSearch?: (search: string) => void;
  hasWarehouse: boolean;
  warehouseName?: string | null;
  positionOptions: StorageSelectOption[];
  hasGarage: boolean;
  garageName?: string | null;
  workstationOptions: StorageSelectOption[];
};

export type PortFormFieldsProps = PortFormLocationMappingProps & {
  address: Coordinates;
  buttonText: string;
  defaultStorageUnits: StorageSelectOption[];
} & (
    | { mode: 'create'; onSubmit: (values: SitePortFormValues) => Promise<void> }
    | {
        mode: 'edit';
        port: SitePortRecordListWithStorage;
        onSubmit: (values: SitePortFormValues & { status: ResourceStatus }) => Promise<void>;
      }
  );

export const PortFormFields = (props: PortFormFieldsProps) => {
  const {
    address,
    buttonText,
    hasStorageFacility,
    storageFacilityName,
    storageAreaOptions,
    storageAreaNameById,
    storageUnitOptions,
    isUnitsPending = false,
    onUnitSearch,
    hasWarehouse,
    warehouseName,
    positionOptions,
    hasGarage,
    garageName,
    workstationOptions,
    defaultStorageUnits
  } = props;
  const port = props.mode === 'edit' ? props.port : null;

  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    port ? { lat: port.latitude, lng: port.longitude } : null
  );
  const [parkingCoords, setParkingCoords] = useState<{ lat: number; lng: number } | null>(
    port?.parkingLatitude != null && port.parkingLongitude != null
      ? { lat: port.parkingLatitude, lng: port.parkingLongitude }
      : null
  );
  const [showParkingMap, setShowParkingMap] = useState(parkingCoords !== null);
  const [coordsError, setCoordsError] = useState<string | null>(null);

  const form = useAppForm({
    defaultValues: {
      name: port?.name ?? '',
      providerInstructions: port?.providerInstructions ?? '',
      parkingInstructions: port?.parkingInstructions ?? '',
      status: port?.status ?? ResourceStatus.ACTIVE,
      bulkStorageAreaIds: [] as number[],
      storageUnits: defaultStorageUnits,
      positionIds: port?.positionIds ?? [],
      workstationIds: port?.workstationIds ?? []
    } satisfies FormValues,
    validators: { onSubmit: validationSchema },
    onSubmit: async ({ value }) => {
      if (!coords) {
        setCoordsError('Please select a location on the map');
        return;
      }
      setCoordsError(null);
      const values: SitePortFormValues = {
        name: value.name,
        providerInstructions: value.providerInstructions,
        parkingInstructions: value.parkingInstructions,
        parkingLatitude: parkingCoords?.lat ?? null,
        parkingLongitude: parkingCoords?.lng ?? null,
        latitude: coords.lat,
        longitude: coords.lng,
        storageUnitIds: value.storageUnits.map((option) => option.value),
        positionIds: value.positionIds,
        workstationIds: value.workstationIds
      };
      if (props.mode === 'edit') {
        await props.onSubmit({ ...values, status: value.status });
        return;
      }
      await props.onSubmit(values);
    }
  });

  const { bulkExpandMessage, isExpandingAreas, onBulkAreasChange } = useStorageAreaBulkExpand({
    storageAreaNameById,
    getSelectedUnits: () => form.getFieldValue('storageUnits'),
    setSelectedUnits: (units) => form.setFieldValue('storageUnits', units)
  });

  const showLocationMapping = hasStorageFacility || hasWarehouse || hasGarage;

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className='flex flex-col gap-5'
      >
        <form.AppField name='name'>
          {(field) => <field.Input id='site-port-name' label='Name' required />}
        </form.AppField>
        <form.AppField name='providerInstructions'>
          {(field) => (
            <field.TextArea id='site-port-provider-instructions' label='Port access and handling instructions' />
          )}
        </form.AppField>
        <form.AppField name='parkingInstructions'>
          {(field) => (
            <field.TextArea id='site-port-parking-instructions' label='Suggested parking for pickup and delivery' />
          )}
        </form.AppField>
        {props.mode === 'edit' && (
          <form.AppField name='status'>
            {(field) => <field.Select label='Status' options={resourceStatusOptions} />}
          </form.AppField>
        )}

        {showLocationMapping && (
          <div className='flex flex-col gap-5'>
            <p className='text-sm text-muted-foreground'>
              Link this port to storage units, warehouse positions, or garage workstations in this building. The same
              location can be linked to several ports.
            </p>
            {hasStorageFacility && (
              <>
                <p className='text-sm text-muted-foreground'>
                  Storage{storageFacilityName ? ` in ${storageFacilityName}` : ''}
                </p>
                {storageAreaOptions.length > 0 && (
                  <form.AppField name='bulkStorageAreaIds'>
                    {(field) => (
                      <field.MultiSelect
                        label='Add units by storage area'
                        options={storageAreaOptions}
                        placeholder='Select areas to add their units'
                        onSelectionSideEffect={(values) => {
                          void onBulkAreasChange(values as number[]);
                        }}
                      />
                    )}
                  </form.AppField>
                )}
                {(bulkExpandMessage || isExpandingAreas) && (
                  <p className='text-sm text-muted-foreground' role='status'>
                    {isExpandingAreas ? 'Loading units from selected areas…' : bulkExpandMessage}
                  </p>
                )}
                <form.AppField name='storageUnits'>
                  {(field) => (
                    <field.AsyncMultiSelect
                      label='Storage units'
                      options={storageUnitOptions}
                      isLoading={isUnitsPending || isExpandingAreas}
                      onInputChange={(search) => onUnitSearch?.(search)}
                      placeholder='Search storage units'
                      hideSelectAll
                    />
                  )}
                </form.AppField>
              </>
            )}
            {hasWarehouse && (
              <>
                <p className='text-sm text-muted-foreground'>
                  Warehouse{warehouseName ? ` ${warehouseName}` : ''} positions
                </p>
                <form.AppField name='positionIds'>
                  {(field) => (
                    <field.MultiSelect
                      label='Positions'
                      options={positionOptions}
                      placeholder='Select warehouse positions'
                    />
                  )}
                </form.AppField>
              </>
            )}
            {hasGarage && workstationOptions.length > 0 && (
              <>
                <p className='text-sm text-muted-foreground'>Garage{garageName ? ` ${garageName}` : ''} workstations</p>
                <form.AppField name='workstationIds'>
                  {(field) => (
                    <field.MultiSelect
                      label='Workstations'
                      options={workstationOptions}
                      placeholder='Select workstations'
                    />
                  )}
                </form.AppField>
              </>
            )}
          </div>
        )}

        <PortPinFields
          address={address}
          coords={coords}
          onCoordsChange={(next) => {
            setCoords(next);
            setCoordsError(null);
          }}
          coordsError={coordsError}
          parkingCoords={parkingCoords}
          onParkingCoordsChange={setParkingCoords}
          showParkingMap={showParkingMap}
          onToggleParkingMap={() => {
            setParkingCoords(null);
            setShowParkingMap(!showParkingMap);
          }}
        />

        <NavioOverlayActions>
          <form.SubscribeButton formId={FORM_ID} label={buttonText} />
        </NavioOverlayActions>
      </form>
    </form.AppForm>
  );
};
